import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, isRef as _isRef, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, TransitionGroup as _TransitionGroup, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "mt-x6" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 2 }

import { computed, watch, WritableComputedRef } from 'vue';
import { Field } from 'vee-validate';
import { B2BExpressPurchaseOption, B2BStandardPurchaseOption, ModuleConfigurationPurchaseOption, PurchaseOptionType } from '@/types/productServerApi';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import { useModuleConfigurator } from '@/shared/product/details/ui/module-configurator/useModuleConfigurator';
import useTEMPB2BFeatureBasket from '@/b2b/basket/useTEMPB2BFeatureBasket';
import InputRadio from '@/shared/form/InputRadio.vue';
import { webApiMessageId } from '@/b2b/product/purchase/useAddToBasket';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductPurchaseOptions',
  emits: ['update'],
  setup(__props, { emit }) {



const labels = useBlockLabels();
const { productData } = useProductDetailData();
const { moduleConfiguratorTileData, is3DConfiguratorFeatureEnabled } = useModuleConfigurator();
const { lineItems, getIsPickAndPack, setPickAndPackBySku } = useTEMPB2BFeatureBasket();

const showSinglePurchaseOption = computed(() => !showMultiplePurchaseOptions.value);
const showMultiplePurchaseOptions = computed(() => productData.value ? productData.value.purchasePossibilities.length > 1 : false);

const purchaseOptions = computed(() => {
    // only to ensure casting of type - shouldn't expect other purchase option types for B2B
    const options = productData.value.purchasePossibilities.filter((option) => option.type === PurchaseOptionType.B2BStandard || option.type === PurchaseOptionType.B2BExpress || option.type === PurchaseOptionType.ModuleConfiguration);

    return (options ?? []) as Array<B2BStandardPurchaseOption | B2BExpressPurchaseOption | ModuleConfigurationPurchaseOption>;
});

const isExpressOnly = computed(() => {
    const isExpress = !!purchaseOptions.value.find((option) => option.type === PurchaseOptionType.B2BExpress);
    return isExpress && purchaseOptions.value.length === 1;
});

const maybeExpressOnlyTitle = computed(() => {
    return isExpressOnly.value ? labels.b2BExpressOnlyDeliveryTitle : labels.b2BExpressDeliveryTitle;
});

const maybeExpressOnlyDescription = computed(() => {
    return isExpressOnly.value ? labels.b2BExpressOnlyDeliveryDescription : labels.b2BExpressDeliveryDescription;
});

const onPurchaseOptionChange = async(option) => {
    await setPickAndPackBySku(productData.value.id, option === PurchaseOptionType.B2BExpress, webApiMessageId);
};

const preferredPurchaseOptionType: WritableComputedRef<PurchaseOptionType> = computed({
    get: () => {
        const item = lineItems.value.find(x => x.sku === productData.value.id);

        if (is3DConfiguratorFeatureEnabled.value && moduleConfiguratorTileData.value?.modules) {
            return PurchaseOptionType.ModuleConfiguration;
        }
        if (getIsPickAndPack(item)) {
            return PurchaseOptionType.B2BExpress;
        }
        return isExpressOnly.value ? PurchaseOptionType.B2BExpress : PurchaseOptionType.B2BStandard;
    },
    // no set as this is a readonly value for VeeValidate
    set() {},
});

watch(preferredPurchaseOptionType, (optionType) => {
    emit('update', optionType);
}, { immediate: true });


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Field), {
      id: "purchaseOptionTypeId",
      modelValue: _unref(preferredPurchaseOptionType),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(preferredPurchaseOptionType) ? (preferredPurchaseOptionType).value = $event : null)),
      name: "purchaseOptionType",
      type: "hidden",
      "aria-hidden": "true",
      readonly: "readonly",
      tabindex: "-1",
      disabled: ""
    }, null, 8, ["modelValue"]),
    _createVNode(_TransitionGroup, { name: "o-anim-fade-300" }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(purchaseOptions), (option) => {
          return (_openBlock(), _createElementBlock("div", {
            key: option.type,
            class: "mb-x3 md:mb-x2 last:mb-0"
          }, [
            (option.type === _unref(PurchaseOptionType).ModuleConfiguration)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2))
              : (option.type === _unref(PurchaseOptionType).B2BExpress)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(InputRadio, {
                      modelValue: _unref(preferredPurchaseOptionType),
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(preferredPurchaseOptionType) ? (preferredPurchaseOptionType).value = $event : null)),
                      disabled: _unref(showSinglePurchaseOption) ? true : null,
                      label: _unref(maybeExpressOnlyTitle),
                      value: option.type,
                      "align-label-with-top": _unref(showMultiplePurchaseOptions),
                      "hide-label": !_unref(showMultiplePurchaseOptions),
                      class: "font-bold",
                      name: "purchaseOptionType",
                      onChange: ($event: any) => (onPurchaseOptionChange(option.type))
                    }, null, 8, ["modelValue", "disabled", "label", "value", "align-label-with-top", "hide-label", "onChange"]),
                    _createElementVNode("span", {
                      class: _normalizeClass({'pl-30': _unref(purchaseOptions).length > 1})
                    }, _toDisplayString(_ctx.$dictionary.formatNamed(_unref(maybeExpressOnlyDescription), { quantity: option.quantityMultiplier })), 3)
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(InputRadio, {
                      modelValue: _unref(preferredPurchaseOptionType),
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(preferredPurchaseOptionType) ? (preferredPurchaseOptionType).value = $event : null)),
                      disabled: _unref(showSinglePurchaseOption) ? true : null,
                      label: _unref(labels)[option.title],
                      value: option.type,
                      "align-label-with-top": _unref(showMultiplePurchaseOptions),
                      "hide-label": !_unref(showMultiplePurchaseOptions),
                      class: "font-bold",
                      name: "purchaseOptionType",
                      onChange: ($event: any) => (onPurchaseOptionChange(option.type))
                    }, null, 8, ["modelValue", "disabled", "label", "value", "align-label-with-top", "hide-label", "onChange"]),
                    _createElementVNode("span", {
                      class: _normalizeClass({'pl-30': _unref(purchaseOptions).length > 1})
                    }, _toDisplayString(_ctx.$dictionary.pluralize(option.quantityMultiplier, _unref(labels).b2BStandardDeliveryDescription, _unref(labels).b2BStandardDeliveryDescriptionPlural, { quantity: option.quantityMultiplier })), 3)
                  ]))
          ]))
        }), 128))
      ]),
      _: 1
    })
  ]))
}
}

})